import { useEffect, useRef, useState } from 'react';

import PropTypes from 'prop-types';
import { BsTelephone, BsEnvelope, BsPencil, BsTrash } from 'react-icons/bs';

import Button from '#globalComponents/Button/Button';
import IconButton from '#globalComponents/IconButton/IconButton';
import ConfirmModal from '#globalComponents/Overlay/ConfirmModal';
import useContactRoles from '#globalHooks/useContactRoles';

import { contactAnchorId } from '../../../utils/crmPaths';
import CustomFieldsSection from '../../common/CustomFieldsSection/CustomFieldsSection';

const ContactRow = ({ contact, onUpdate, onDelete, aircallConnected, onAircallDial, highlighted = false }) => {
	const { roleLabel } = useContactRoles();
	const displayName = contact.fullName ?? [contact.firstName, contact.lastName].filter(Boolean).join(' ') ?? '';
	const [editing, setEditing] = useState(false);
	const [deleteOpen, setDeleteOpen] = useState(false);
	const [deleting, setDeleting] = useState(false);
	const [draft, setDraft] = useState({});
	const rowRef = useRef(null);

	// The deal details and the activity panel render after the first paint and push the row down,
	// so a single scroll on mount lands too high; re-anchor once they are in.
	useEffect(() => {
		if (!highlighted) return undefined;
		const scrollToRow = () => rowRef.current?.scrollIntoView({ block: 'center' });
		scrollToRow();
		const timer = setTimeout(scrollToRow, 600);
		return () => clearTimeout(timer);
	}, [highlighted]);

	const startEdit = () => {
		setDraft({ _customFields: { ...(contact.customFields ?? {}) } });
		setEditing(true);
	};

	const handleSave = () => {
		setEditing(false);
		const patch = {};
		// Collect default field changes from draft
		for (const [key, value] of Object.entries(draft)) {
			if (key === '_customFields') continue;
			if (value !== (contact[key] ?? '')) {
				patch[key] = value || null;
			}
		}
		// Collect custom field changes
		const origCf = contact.customFields ?? {};
		const draftCf = draft._customFields ?? {};
		if (JSON.stringify(origCf) !== JSON.stringify(draftCf)) {
			patch.customFields = draftCf;
		}
		if (Object.keys(patch).length > 0) onUpdate(contact.id, patch);
	};

	const cancelEdit = () => setEditing(false);

	const initials = (() => {
		const first = (contact.firstName || displayName || '?')[0]?.toUpperCase() ?? '';
		const last = (contact.lastName || '')[0]?.toUpperCase() ?? '';
		return first + last;
	})();

	return (
		<div
			ref={rowRef}
			id={contactAnchorId(contact.id)}
			className={`relative border-b border-bgBase-400 last:border-b-0 transition-colors ${highlighted ? 'bg-primary-100' : editing ? 'bg-primary-100/40' : ''}`}
		>
			<div className="flex items-start gap-2.5 px-4 py-3 group">
				<div className="h-7 w-7 rounded-full bg-bgBase-400 text-bgBase-800 flex items-center justify-center text-[11px] font-semibold flex-shrink-0 mt-0.5">
					{initials}
				</div>
				<div className="min-w-0 flex-1">
					{editing ? (
						<div className="space-y-1.5">
							<CustomFieldsSection
								entityType="contact"
								entity={{ ...contact, ...draft }}
								values={draft._customFields ?? contact.customFields ?? {}}
								inline
								showLabel={false}
								onChangeEntity={(fieldKey, value) => setDraft((d) => ({ ...d, [fieldKey]: value }))}
								onChange={(fieldId, value) =>
									setDraft((d) => ({
										...d,
										_customFields: { ...(d._customFields ?? {}), [fieldId]: value },
									}))
								}
							/>
							<div className="flex gap-1.5 pt-1 justify-end">
								<Button type="button" variant="outline" size="sm" onClick={cancelEdit}>
									Abbrechen
								</Button>
								<Button type="button" variant="solid" size="sm" onClick={handleSave}>
									Speichern
								</Button>
							</div>
						</div>
					) : (
						<>
							<div className="flex items-center gap-1.5 min-w-0">
								<span className="text-[12px] font-semibold text-text truncate">
									{displayName || contact.emailAddress || 'Unbekannt'}
								</span>
								{contact.isPrimary && (
									<span className="text-[9px] font-semibold uppercase tracking-wider bg-primary-200 text-primary-500 px-1.5 py-0.5 rounded-full flex-shrink-0">
										primary
									</span>
								)}
							</div>
							{contact.companyRole && (
								<p className="text-[10px] text-bgBase-800 mt-0.5 truncate">
									{roleLabel(contact.companyRole)}
								</p>
							)}
							<div className="flex flex-col gap-0.5 mt-1.5">
								{contact.emailAddress && (
									<a
										href={`mailto:${contact.emailAddress}`}
										className="text-[10px] text-bgBase-800 hover:text-primary-600 inline-flex items-center gap-1 transition-colors truncate"
									>
										<BsEnvelope className="w-2.5 h-2.5 text-bgBase-800 flex-shrink-0" />{' '}
										<span className="truncate">{contact.emailAddress.split('@')[0]}@...</span>
									</a>
								)}
								{contact.phoneNumber && aircallConnected && (
									<button
										type="button"
										onClick={() => onAircallDial(contact.phoneNumber)}
										className="text-[10px] text-primary-700 hover:text-primary-600 inline-flex items-center gap-1 transition-colors truncate"
									>
										<BsTelephone className="w-2.5 h-2.5 text-primary-500 flex-shrink-0" />{' '}
										<span className="truncate">{contact.phoneNumber}</span>
									</button>
								)}
								{contact.phoneNumber && !aircallConnected && (
									<a
										href={`tel:${contact.phoneNumber}`}
										className="text-[10px] text-bgBase-800 hover:text-primary-600 inline-flex items-center gap-1 transition-colors truncate"
									>
										<BsTelephone className="w-2.5 h-2.5 text-bgBase-800 flex-shrink-0" />{' '}
										<span className="truncate">{contact.phoneNumber}</span>
									</a>
								)}
							</div>
						</>
					)}
				</div>
				{!editing && (
					<div className="absolute top-2 right-3 flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
						<IconButton
							icon={BsPencil}
							label="Bearbeiten"
							tone="primary"
							onClick={startEdit}
							className="h-6 w-6"
						/>
						{onDelete && (
							<IconButton
								icon={BsTrash}
								label="Löschen"
								tone="danger"
								onClick={() => setDeleteOpen(true)}
								className="h-6 w-6"
							/>
						)}
					</div>
				)}
			</div>

			<ConfirmModal
				isOpen={deleteOpen}
				onClose={() => setDeleteOpen(false)}
				onConfirm={async () => {
					setDeleting(true);
					try {
						await onDelete(contact.id);
					} finally {
						setDeleting(false);
						setDeleteOpen(false);
					}
				}}
				title={`Kontakt "${displayName || contact.emailAddress || 'Unbekannt'}" löschen`}
				message="Möchten Sie diesen Kontakt wirklich löschen?"
				confirmText="Löschen"
				isLoading={deleting}
			/>
		</div>
	);
};

ContactRow.propTypes = {
	/** Scrolled-to contact of a `#contact-<id>` deep link. */
	highlighted: PropTypes.bool,
	contact: PropTypes.object.isRequired,
	onUpdate: PropTypes.func.isRequired,
	onDelete: PropTypes.func,
	aircallConnected: PropTypes.bool,
	onAircallDial: PropTypes.func,
};

export default ContactRow;
